import queryString from 'query-string';
import { generatePath } from 'react-router-dom';

import { ACH_CAMPAIGN_READINESS_URL } from '../../constants/ach/apiUrls';
import { achGetJSON } from './client';

// Types are grounded to the ACH readiness wire DTO
// (internal/transport/http/campaignreadiness/map.go — the CampaignReadinessEnvelope
// huma renders directly as the response body). No invented fields.

export type ReadinessStatus = 'MET' | 'UNMET' | 'NOT_EVALUATED';

// EntityRefDTO — the entity a condition points at, plus a server-stamped relative
// admin-SPA href (transport-only).
export interface ReadinessEntityRef {
  type: string;
  id: string;
  name: string;
  href: string;
}

// ConditionContextDTO — typed context payload. Entities is always present; the
// per-family scalars are omitempty on the wire, hence optional here.
export interface ReadinessConditionContext {
  entities: ReadinessEntityRef[];
  url?: string;
  macro?: string;
  index?: number;
  count?: number;
  max?: number;
  field?: string;
  value?: string;
  start_date?: string;
  end_date?: string;
  observed?: string;
}

// ConditionDTO — one structured readiness condition. code is the
// "<requirement_slug>.<local_code>" wire code.
export interface ReadinessCondition {
  code: string;
  message: string;
  context: ReadinessConditionContext;
}

// RequirementResultDTO — one readiness requirement's verdict. reason is the flat
// "; "-joined condition messages ("" iff MET); conditions carries the structured
// payload (always an array, [] when MET/absent).
export interface ReadinessRequirement {
  id: string;
  label: string;
  status: ReadinessStatus;
  reason: string;
  conditions: ReadinessCondition[];
}

// CampaignReadinessEnvelope — the full response body. ready is the collect-all
// verdict (true iff every requirement is MET), independent of the verbose filter.
// In the default (non-verbose) view the requirements array holds only the
// actionable rows (UNMET + NOT_EVALUATED); verbose adds the MET rows too.
export interface CampaignReadiness {
  campaign_id: number;
  ready: boolean;
  requirements: ReadinessRequirement[];
}

// getCampaignReadiness fetches a campaign's (== AdGroup's) publish-readiness via the
// Tier-A ACH client. Default (verbose=false) returns only actionable requirements —
// the smallest payload that still explains a not-ready verdict. On any transport
// failure achGetJSON throws; callers (useCampaignReadiness) degrade gracefully.
export const getCampaignReadiness = (
  achApiHost: string,
  campaignId: number,
  verbose = false
): Promise<CampaignReadiness> => {
  const path = generatePath(ACH_CAMPAIGN_READINESS_URL, { id: String(campaignId) });
  const url = verbose ? `${path}?${queryString.stringify({ verbose: true })}` : path;
  return achGetJSON(achApiHost, url) as Promise<CampaignReadiness>;
};
